import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { imageListSchema, imageSchema, type Image } from '@competa/shared';
import { apiRequest, apiUrl } from '../../api/client';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import dialogStyles from '../../components/Dialog.module.css';
import { TextField } from '../../components/TextField';
import { errorMessage } from '../../i18n/errors';
import styles from '../competitions/Competitions.module.css';

/** Eigenschaften des Dialogs „Bild zusammenführen". */
interface MergeDialogProps {
  /** Das Bild, das in ein anderes überführt und danach gelöscht wird (z. B. ein Tippfehler im Titel). */
  source: Image | null;
  onClose: () => void;
}

/**
 * Dialog zum Zusammenführen zweier Bilder: Das gewählte Bild (Ausgang) wird mit allen Einreichungen in ein
 * Zielbild überführt und gelöscht. Sind beide im selben Wettbewerb eingereicht, bleibt das bessere Ergebnis.
 * Typischer Fall: ein Titeleintrag mit Tippfehler aus dem Altbestand.
 * @param props Ausgangsbild und Schließen-Rückruf.
 */
export function MergeDialog({ source, onClose }: MergeDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [target, setTarget] = useState<Image | null>(null);

  const images = useQuery({
    queryKey: ['images', 'merge', q],
    queryFn: () =>
      apiRequest('GET', `/images?limit=60${q ? `&q=${encodeURIComponent(q)}` : ''}`, {
        schema: imageListSchema,
      }),
    enabled: source !== null,
  });
  const merge = useMutation({
    mutationFn: () =>
      apiRequest('POST', `/images/${source!.id}/merge`, {
        body: { targetId: target!.id },
        schema: imageSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['images'] });
      await queryClient.invalidateQueries({ queryKey: ['competitions'] });
      await queryClient.invalidateQueries({ queryKey: ['competition'] });
      await queryClient.invalidateQueries({ queryKey: ['analytics'] });
      close();
    },
  });

  /** Schließt den Dialog und setzt die Eingaben zurück. */
  function close(): void {
    setQ('');
    setTarget(null);
    merge.reset();
    onClose();
  }

  const candidates = images.data?.items.filter((image) => image.id !== source?.id) ?? [];
  return (
    <Dialog
      title={t('images.mergeTitle', { name: source?.title ?? '' })}
      isOpen={source !== null}
      onClose={close}
    >
      {target === null ? (
        <>
          <p>{t('images.mergeHint')}</p>
          <TextField
            label={t('images.mergeSearch')}
            type="search"
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />
          {images.isPending && <p role="status">{t('common.loading')}</p>}
          <ul className={styles.grid}>
            {candidates.map((image) => (
              <li key={image.id} className={styles.tile}>
                {image.hasThumb && (
                  <img
                    className={styles.tileImage}
                    src={apiUrl(`/images/${image.id}/thumb`)}
                    alt=""
                    loading="lazy"
                  />
                )}
                <span className={styles.tileTitle}>{image.title}</span>
                <Button
                  variant="secondary"
                  aria-label={t('images.mergeInto', { name: image.title })}
                  onClick={() => setTarget(image)}
                >
                  {t('images.mergeChoose')}
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p>{t('images.mergeConfirm', { from: source?.title ?? '', into: target.title })}</p>
      )}
      {merge.error && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, merge.error)}
        </p>
      )}
      <div className={dialogStyles.actions}>
        <Button variant="secondary" onClick={target ? () => setTarget(null) : close}>
          {target ? t('images.mergeBack') : t('common.cancel')}
        </Button>
        {target && (
          <Button disabled={merge.isPending} onClick={() => merge.mutate()}>
            {t('images.mergeDo')}
          </Button>
        )}
      </div>
    </Dialog>
  );
}
